import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { Button } from "@primitives/button";
import { Input } from "@primitives/input";

import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

import { loginUser, logoutUser } from "@/middleware/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { readLoginReturnPath } from "@/lib/loginReturnPath";
import { LoginFormSchema, LoginFormType } from "@/schemas/auth";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/server/apiError";
import { MAX_PASSWORD_LENGTH } from "@/config/constants";
import { useTurnstile } from "./hooks/useTurnstile";

export default function LoginForm() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Where to land after login. An auth guard (today: the standalone meet
  // routes) may have bounced the user here with a validated return path in
  // router state; default stays "/" for every other login.
  const location = useLocation();
  const returnPath = readLoginReturnPath(location.state) ?? "/";
  const [showPassword, setShowPassword] = useState(false);
  const {
    ref,
    token,
    isReady,
    showError,
    enabled: turnstileEnabled,
    status,
    siteKey,
    onSuccess,
    onError,
    onExpire,
    onBeforeInteractive,
    onAfterInteractive,
    reset,
  } = useTurnstile();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const form = useForm<LoginFormType>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  async function onSubmit(values: LoginFormType) {
    try {
      const { pendingTc } = await loginUser(values, token);
      if (!pendingTc) {
        navigate(returnPath, { replace: true });
      }
      // pendingTc=true: stay on login page — TcGate modal will appear on top
    } catch (error) {
      logoutUser();
      if (error instanceof ApiError) {
        // Array form: fall back to the generic message when the title has no
        // translation, instead of rendering the raw key.
        const message = t([
          error.data?.title ?? "errorOccurred",
          "errorOccurred",
        ]);
        toast({
          duration: 1000,
          description: message,
          variant: "destructive",
        });
      } else {
        let message =
          error instanceof Error ? error.message : t("errorOccurred");
        try {
          const parsed = JSON.parse(message) as { title?: string };
          if (parsed.title) {
            message = t(`errors.${parsed.title}`);
          }
        } catch {
          // not JSON — use message as-is
        }
        toast({
          duration: 1000,
          description: message,
          variant: "destructive",
        });
      }
    } finally {
      reset();
    }
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="my-7 space-y-2">
        <div className="flex flex-col gap-5">
          <FormField
            control={form.control}
            // disabled={form.formState.isSubmitting}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="text"
                    className="rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                    placeholder={t("login.username")}
                    autoComplete="username"
                    autoCapitalize="off"
                    autoCorrect="off"
                    {...field}
                    onChange={(e) => {
                      // Transform input to lowercase before updating form state
                      const lowercaseValue = e.target.value.toLowerCase();
                      field.onChange(lowercaseValue);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            // disabled={form.formState.isSubmitting}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <Input
                      className="rounded-lg border-border bg-card py-6 pr-9 placeholder:opacity-70"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("login.password")}
                      autoComplete="current-password"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={MAX_PASSWORD_LENGTH}
                      {...field}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <EyeIcon className="size-5 text-foreground/50" />
                      ) : (
                        <EyeOffIcon className="size-5 text-foreground/50" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {turnstileEnabled && (
          <Turnstile
            className="[&>div>iframe]:!w-full [&>div]:!w-full"
            style={{ width: "100%" }}
            ref={ref}
            siteKey={siteKey}
            options={{ appearance: "interaction-only", size: "flexible" }}
            onSuccess={onSuccess}
            onError={onError}
            onExpire={onExpire}
            onBeforeInteractive={onBeforeInteractive}
            onAfterInteractive={onAfterInteractive}
          />
        )}

        {showError && (
          <p role="alert" className="text-sm text-destructive">
            {t("errors.Turnstile.Blocked")}
          </p>
        )}

        <div>
          <Button
            disabled={form.formState.isSubmitting || !isReady}
            type="submit"
            className="mt-10 h-12 w-full rounded-lg"
          >
            {form.formState.isSubmitting
              ? t("login.loggingIn")
              : status === "loading"
                ? t("login.verifying")
                : t("login.title")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
